import { Combobox } from "@headlessui/react";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { FnrPopover } from "UI/FnrPopover2";

import { ComboboxProvider } from "./ComboxContext";
import {
  Button,
  HeadlessButton,
  HoverIcon,
  Icon,
  Input,
  InputContainer,
  OptionInner,
  Options,
} from "./Components/Shared";

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.placeholder
 * @param {number | number[]} props.selection
 * @param {function} props.onSelect
 * @param {array} props.options
 * @param {string} props.icon - the full icon string, like 'far fa-user'
 * @param {boolean} props.multiselect - If set to true, props.selection needs to be an array and onSelect needs to handle an array
 * @param {boolean} props.openIndicator - If set to true, will show rotating chevron icon on the right side of the button
 * @param {string} props.focusColor
 * @param {string} props.focusSize
 * @param {string} props.selectionTextAlign - 'start' | 'center' | 'end'
 * @param {string} props.iconColor
 * @param {string} props.iconSize
 * @param {object} props.style - the button styles
 * @param {function} props.onButtonClick - called when the button is clicked
 * @param {string} props.className
 * @param {boolean} props.isButtonContainerFullWidth
 * @param {object} props.placeholderStyle - the placeholder styles
 * @param {boolean} props.disabled
 * @param {boolean} props.controlled - if true, the selection can be updated from the parent component
 */
function ComboboxContent({
  label,
  placeholder,
  selection = [],
  onSelect,
  options = [],
  icon,
  multiselect,
  openIndicator = true,
  selectionTextAlign,
  focusColor,
  focusSize,
  iconColor,
  iconSize,
  style,
  onButtonClick,
  className,
  isButtonContainerFullWidth = false,
  preventDefault = false,
  placeholderStyle,
  disabled,
  controlled
}) {
  const defaultSelection = multiselect
    ? selection
    : Array.isArray(selection)
    ? selection[0]
    : selection;
  const buttonRef = useRef();
  const visibleButtonRef = useRef();
  const [internalSelection, setInternalSelection] = useState(defaultSelection);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    if (controlled) { 
      setInternalSelection(defaultSelection);
    }
  }, [defaultSelection, controlled]);

  const filteredOptions = options.filter((option) => {
    let hasFilterText = filterText?.length > 0;
    if (!hasFilterText) return true;
    return option.value.toLowerCase().includes(filterText?.toLowerCase());
  });

  const hideInput = filterText?.length <= 0;

  function handleSelect(selection) {
    setInternalSelection(selection);
    visibleButtonRef.current.focus();
    onSelect(selection);
  }

  function updateFilter(event) {
    setFilterText(event.target.value);
  }

  function getSelectionDisplay() {
    // selection can be 0. So just a falsy evaluator isn't enough
    if (
      (!(typeof selection === "number" && selection === 0) && !selection) ||
      selection === []
    )
      return null;

    if (typeof selection === "number") {
      // get value of a single selection
      return options.find((option) => option.id === selection)?.value;
    }

    // create string of all the values combined
    let combinedValues = options
      .filter((option) => selection.includes(option.id))
      .map((option) => option.value)
      .join(", ");

    return combinedValues;
  }

  const buttonContainerStyle = {
    position: "relative",
  };
  if (isButtonContainerFullWidth) {
    buttonContainerStyle.width = "100%";
  }

  return (
    <Combobox
      value={internalSelection}
      onChange={handleSelect}
      multiple={multiselect}
      disabled={disabled}
    >
      {({ open, value }) => (
        <>
          <div style={buttonContainerStyle}>
            <HeadlessButton ref={buttonRef}></HeadlessButton>
            <VisibleButton
              ref={visibleButtonRef}
              className={className}
              onClick={(e) => {
                if (preventDefault) {
                  e.preventDefault();
                }
                if (onButtonClick) onButtonClick({ event: e, open, value });
                buttonRef.current.click();
              }}
              style={style}
              focusColor={focusColor}
              disabled={disabled}
              type="button"
            >
              {icon?.length > 0 && (
                <Icon className={icon} color={iconColor}></Icon>
              )}
              <Span
                className={`truncate`}
                lightText={!getSelectionDisplay() && !label && placeholder}
                selectionTextAlign={selectionTextAlign}
                textColor={style?.color}
                style={placeholderStyle}
              >
                {getSelectionDisplay() || label || placeholder || ""}
              </Span>
              {openIndicator && (
                <Icon
                  rotated={open}
                  className="fas fa-chevron-down"
                  color={iconColor}
                  size={iconSize}
                ></Icon>
              )}
            </VisibleButton>
          </div>

          <FnrPopover
            show={open}
            buttonRef={buttonRef}
            location="bottom"
            onClose={() => {
              setFilterText("");
            }}
          >
            {open && (
              <div style={{ position: "relative" }}>
                <InputContainer
                  $hideInput={hideInput}
                  initial={{
                    opacity: 0,
                    height: "0px",
                  }}
                  animate={{
                    opacity: hideInput ? 0 : 1,
                    height: hideInput ? "0px" : "auto",
                  }}
                >
                  <Combobox.Input as={Fragment}>
                    <Input
                      value={filterText}
                      onChange={updateFilter}
                      // hideInput={hideInput}
                    />
                  </Combobox.Input>
                  {!hideInput && (
                    <HoverIcon
                      className="fas fa-times-circle"
                      onClick={() => setFilterText("")}
                    />
                  )}
                </InputContainer>

                <Options static={true}>
                  {filteredOptions.map((option) => (
                    <Combobox.Option key={option.id} value={option.id}>
                      {({ active, selected }) => (
                        <OptionInner active={active} selected={selected}>
                          <span>
                            {option.value}
                            {option.info ? option.info : null}
                          </span>
                          <Icon
                            className={`fas fa-check ${
                              !selected ? "hide-icon" : ""
                            }`}
                          />
                        </OptionInner>
                      )}
                    </Combobox.Option>
                  ))}
                </Options>
              </div>
            )}
          </FnrPopover>
        </>
      )}
    </Combobox>
  );
}

export default function FnrCombobox(props) {
  return (
    <ComboboxProvider>
      <ComboboxContent {...props} />
    </ComboboxProvider>
  );
}

const Span = styled.span`
  color: ${(props) =>
    props?.textColor ? props.textColor : props.lightText ? "#333" : "#000"};
  opacity: ${(props) => (props.lightText ? 0.7 : 1)};
  text-align: ${(props) => props?.selectionTextAlign};

  &:empty:before {
    content: "empty";
    opacity: 0;
  }
`;

const VisibleButton = styled(Button)`
  pointer-events: none; // lets the combobox button be clicked, but can still be focused
`;

FnrCombobox.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  selection: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.string,
    }),
  ]),
  onSelect: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.string,
    })
  ).isRequired,
  icon: PropTypes.oneOfType([
    PropTypes.bool, //if false
    PropTypes.string, // font awesome icon classes, ex: "fas fa-check"
  ]),
  multiselect: PropTypes.bool,
  openIndicator: PropTypes.bool,
  selectionTextAlign: PropTypes.oneOf(["start", "center", "end"]),
  focusColor: PropTypes.string,
  focusSize: PropTypes.string,
  iconColor: PropTypes.string,
  style: PropTypes.object,
  isButtonContainerFullWidth: PropTypes.bool,
};